'use client';
import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import Skeleton from '../../components/Skeleton';
import TileData from '../../components/TileData';
import { AboutYouLabels } from '../lib/labels';

import { Button, Input } from '@components/Form';
import { toastError, toastSuccess } from '@components/root/NotificationProxy';
import { useUserStore } from '@utils/user';

const schema = z.object({
  firstName: z.string().max(50),
  lastName: z.string().max(100),
});

interface Props {
  labels: AboutYouLabels;
}

const AboutYouForm = ({ labels }: Props) => {
  const userStore = useUserStore();
  const [isLoading, setLoading] = useState<boolean>(false);
  const {
    handleSubmit,
    register,
    formState: { errors },
  } = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema),
    defaultValues: {
      firstName: userStore.user?.firstName,
      lastName: userStore.user?.lastName,
    },
  });

  const onSubmit = async (formData) => {
    setLoading(true);

    try {
      await fetch(
        `${process.env.NEXT_PUBLIC_API_PUBLIC}/users/profile/edit`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${userStore.accessToken}`,
          },
          body: JSON.stringify(formData),
        },
      );

      await userStore.fetchUser();
      // TODO: update user data in session

      toastSuccess(labels.toast.success);
    } catch (e) {
      toastError(labels.toast.error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <TileData title={labels.title}>
      <form className="flex h-full flex-col gap-3" onSubmit={handleSubmit(onSubmit)}>
        <label htmlFor="firstName">
          {labels.name}
          <Input
            error={!!errors.firstName}
            placeholder={labels.name}
            {...register('firstName')}
          />
        </label>
        <label htmlFor="lastName">
          {labels.surname}
          <Input
            error={!!errors.lastName}
            placeholder={labels.surname}
            {...register('lastName')}
          />
        </label>
        <Button className="mt-auto" loading={isLoading}>
          {labels.save}
        </Button>
      </form>
    </TileData>
  );
}

const AboutYou = ({ labels }: Props) => {
  const userStore = useUserStore();
  if (!userStore.isReady || !userStore.user) {
    return <Skeleton />;
  }

  return <AboutYouForm labels={labels} />;

};

export default AboutYou;
